import * as Sentry from '@sentry/browser';
import { getDeploymentEnvironment } from 'utils/deploymentEnvironment';

export const AUTH_TELEMETRY_EVENTS = [
  'auth.renewal.started',
  'auth.renewal.joined',
  'auth.renewal.lock',
  'auth.renewal.adopted_generation',
  'auth.renewal.forced_exchange',
  'auth.renewal.generation_published',
  'auth.renewal.succeeded',
  'auth.renewal.failed',
  'auth.request.401_recovered',
  'auth.request.403',
  'auth.reauthentication.required',
  'auth.pkce.callback_failed',
  'auth.pkce.storage_quota',
  'auth.local_work_backup.failed',
  'auth.logout.explicit',
] as const;

export const AUTH_TELEMETRY_FIELDS = [
  'buildGitSha',
  'originLabel',
  'attemptId',
  'generationId',
  'previousGenerationId',
  'trigger',
  'endpointTemplate',
  'httpStatus',
  'reason',
  'remainingLifetimeMs',
  'visibilityState',
  'online',
  'durationMs',
  'retryCount',
  'lockMechanism',
  'lockStage',
  'adoptedFrom',
] as const;

export type AuthTelemetryEventName = (typeof AUTH_TELEMETRY_EVENTS)[number];
type AuthTelemetryField = (typeof AUTH_TELEMETRY_FIELDS)[number];
type AuthTelemetryValue = string | number | boolean | null;
export type AuthTelemetryData = Partial<Record<AuthTelemetryField, AuthTelemetryValue>>;

const ALLOWED_FIELDS = new Set<string>(AUTH_TELEMETRY_FIELDS);
const JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const UNSAFE_STRING_PATTERN = /(?:auth0\||@|[?&=])/i;
const IDENTIFIER_PATH_SEGMENT_PATTERN = /^(?:\d+|[0-9a-f]{8}-[0-9a-f-]{27,})$/i;

const isSafeValue = (value: unknown): value is AuthTelemetryValue => {
  if (value === null || typeof value === 'number' || typeof value === 'boolean') {
    return value === null || typeof value !== 'number' || Number.isFinite(value);
  }
  return typeof value === 'string' && !JWT_PATTERN.test(value) && !UNSAFE_STRING_PATTERN.test(value);
};

export const sanitizeAuthTelemetryData = (data: Record<string, unknown>): AuthTelemetryData =>
  Object.fromEntries(
    Object.entries(data).filter(([key, value]) => ALLOWED_FIELDS.has(key) && isSafeValue(value))
  ) as AuthTelemetryData;

export const sanitizeAuthEndpoint = (endpoint: string): string => {
  const path = endpoint.split(/[?#]/, 1)[0];
  return path
    .split('/')
    .map((segment) => (IDENTIFIER_PATH_SEGMENT_PATTERN.test(segment) ? ':id' : segment))
    .join('/');
};

const getBuildGitSha = (): string =>
  typeof window !== 'undefined' && window.__BUILD_INFO__?.gitSha ? window.__BUILD_INFO__.gitSha : 'unknown';

const getOriginLabel = (): string => getDeploymentEnvironment() ?? 'unknown';

export const emitAuthTelemetry = (event: AuthTelemetryEventName, data: Record<string, unknown> = {}): void => {
  Sentry.addBreadcrumb({
    category: event,
    level: event.endsWith('.failed') ? 'warning' : 'info',
    data: sanitizeAuthTelemetryData({
      buildGitSha: getBuildGitSha(),
      originLabel: getOriginLabel(),
      ...data,
    }),
  });
};
